import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, History, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Badge } from '@/components/ui/badge';
import { usePracticeStore } from '@/store/practiceStore';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';
import { useClientStore } from '@/store/clientStore';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';

export default function HistoricalPracticeFormPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [formData, setFormData] = useState({
    typeId: '',
    practiceNumber: '',
    title: '',
    description: '',
    clientId: '',
    priority: 'MEDIUM',
    status: 'COMPLETED', // Default per pratiche storiche
    startDate: '',
    dueDate: '',
    completedAt: '',
    amount: '',
    paidAmount: '',
    isPaid: false,
    invoiceNumber: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createPractice } = usePracticeStore();

  const {
    practiceTypes,
    fetchPracticeTypes,
    loading: typesLoading,
  } = usePracticeTypeStore();

  const {
    clients,
    fetchClients,
    isLoading: clientsLoading,
  } = useClientStore();

  useEffect(() => {
    fetchPracticeTypes(false);
    fetchClients({ page: 1, limit: 1000 });
  }, [fetchPracticeTypes, fetchClients]);

  // Genera automaticamente il titolo
  useEffect(() => {
    if (formData.typeId && formData.clientId) {
      const type = practiceTypes.find(t => t.id === formData.typeId);
      const client = clients.find(c => c.id === formData.clientId);
      if (type && client) {
        const autoTitle = `${type.code || type.name} - ${client.firstName} ${client.lastName}`;
        setFormData(prev => {
          if (!prev.title || prev.title.startsWith(`${type.code || type.name} -`)) {
            return { ...prev, title: autoTitle };
          }
          return prev;
        });
      }
    }
  }, [formData.typeId, formData.clientId, practiceTypes, clients]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validazione base
    if (!formData.typeId) {
      toast.error('Seleziona un tipo pratica');
      return;
    }
    if (!formData.clientId) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Inserisci un titolo');
      return;
    }

    setIsSubmitting(true);

    try {
      const practiceData: any = {
        typeId: formData.typeId,
        practiceNumber: formData.practiceNumber || undefined,
        title: formData.title,
        description: formData.description || undefined,
        clientId: formData.clientId,
        priority: formData.priority || 'MEDIUM',
        status: formData.status || 'COMPLETED',
        isHistorical: true, // Flag per il backend
      };

      // Date - nessun controllo se nel passato
      if (formData.startDate) {
        practiceData.startDate = new Date(formData.startDate).toISOString();
      }
      if (formData.dueDate) {
        practiceData.dueDate = new Date(formData.dueDate).toISOString();
      }
      if (formData.completedAt) {
        practiceData.completedAt = new Date(formData.completedAt).toISOString();
      }

      // Dati finanziari
      if (formData.amount) {
        practiceData.amount = parseFloat(formData.amount);
      }
      if (formData.paidAmount) {
        practiceData.paidAmount = parseFloat(formData.paidAmount);
      }
      practiceData.isPaid = formData.isPaid;
      if (formData.invoiceNumber) {
        practiceData.invoiceNumber = formData.invoiceNumber;
      }
      if (formData.notes) {
        practiceData.notes = formData.notes;
      }

      const newPractice = await createPractice(practiceData);
      toast.success('Pratica storica importata con successo');
      navigate(`/practices/${newPractice.id}`);
    } catch (error: any) {
      console.error('Error creating historical practice:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'importazione della pratica');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedType = practiceTypes.find(t => t.id === formData.typeId);
  const selectedClient = clients.find(c => c.id === formData.clientId);

  return (
    <div className="w-full min-w-0 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/practices')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle pratiche
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Importa Pratica Storica</h1>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <History className="h-3 w-3 mr-1" />
                Storica
              </Badge>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Inserisci una pratica già esistente senza controlli sulle date
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Informazioni Base */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Informazioni Pratica
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="typeId">Tipo Pratica *</Label>
                  {typesLoading ? (
                    <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      Caricamento...
                    </div>
                  ) : (
                    <Combobox
                      options={practiceTypes
                        .filter(type => type.isActive)
                        .sort((a, b) => a.order - b.order)
                        .map((type) => ({
                          value: type.id,
                          label: `${type.icon || ''} ${type.name}`.trim(),
                          description: type.description || undefined,
                        }))}
                      value={formData.typeId}
                      onValueChange={(value) => setFormData({ ...formData, typeId: value })}
                      placeholder="Seleziona tipo pratica"
                      searchPlaceholder="Cerca tipo pratica..."
                      emptyMessage="Nessun tipo pratica trovato"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="practiceNumber">Numero Pratica (Faldone)</Label>
                  <Input
                    id="practiceNumber"
                    value={formData.practiceNumber}
                    onChange={(e) => setFormData({ ...formData, practiceNumber: e.target.value.toUpperCase() })}
                    placeholder="Es: A12345"
                    maxLength={6}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="clientId">Cliente *</Label>
                {clientsLoading ? (
                  <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    Caricamento...
                  </div>
                ) : (
                  <Combobox
                    options={clients.map(client => ({
                      value: client.id,
                      label: `${client.firstName} ${client.lastName}`,
                      description: client.email || undefined,
                    }))}
                    value={formData.clientId}
                    onValueChange={(value) => setFormData({ ...formData, clientId: value })}
                    placeholder="Seleziona cliente"
                    searchPlaceholder="Cerca cliente..."
                    emptyMessage="Nessun cliente trovato"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="title">Titolo Pratica *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Es: SCIA per ristrutturazione Via Roma 123"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dettagliata della pratica..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stato e Priorità */}
          <Card>
            <CardHeader>
              <CardTitle>Stato della Pratica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Stato *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING">In Attesa</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Corso</SelectItem>
                      <SelectItem value="SUSPENDED">Sospesa</SelectItem>
                      <SelectItem value="WAITING_DOCUMENTS">Attesa Documenti</SelectItem>
                      <SelectItem value="WAITING_APPROVAL">Attesa Approvazione</SelectItem>
                      <SelectItem value="COMPLETED">Completata</SelectItem>
                      <SelectItem value="ARCHIVED">Archiviata</SelectItem>
                      <SelectItem value="CANCELLED">Annullata</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priorità</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Bassa</SelectItem>
                      <SelectItem value="MEDIUM">Media</SelectItem>
                      <SelectItem value="HIGH">Alta</SelectItem>
                      <SelectItem value="URGENT">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date */}
          <Card>
            <CardHeader>
              <CardTitle>Date</CardTitle>
              <p className="text-sm text-gray-500">
                Per pratiche storiche puoi inserire date nel passato senza restrizioni
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="startDate">Data Inizio</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="dueDate">Data Scadenza</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="completedAt">Data Completamento</Label>
                  <Input
                    id="completedAt"
                    type="date"
                    value={formData.completedAt}
                    onChange={(e) => setFormData({ ...formData, completedAt: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Solo se la pratica è stata completata
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dati Finanziari */}
          {user?.canAccessQuotesAndAccounts && (
          <Card>
            <CardHeader>
              <CardTitle>Dati Finanziari (Opzionale)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="amount">Importo Totale (€)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="paidAmount">Importo Pagato (€)</Label>
                  <Input
                    id="paidAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="invoiceNumber">Numero Fattura</Label>
                  <Input
                    id="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                    placeholder="FT-2024-001"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPaid"
                  checked={formData.isPaid}
                  onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="isPaid" className="cursor-pointer">
                  Pratica completamente saldata
                </Label>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Note */}
          <Card>
            <CardHeader>
              <CardTitle>Note</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive sulla pratica..."
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Riepilogo */}
          {formData.title && formData.clientId && formData.typeId && (
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <History className="h-5 w-5" />
                  Riepilogo Pratica Storica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Tipo:</span>
                    <span className="ml-2 font-medium">{selectedType?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Cliente:</span>
                    <span className="ml-2 font-medium">
                      {selectedClient?.firstName} {selectedClient?.lastName}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Titolo:</span>
                    <span className="ml-2 font-medium">{formData.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Stato:</span>
                    <span className="ml-2 font-medium">{formData.status}</span>
                  </div>
                  {formData.startDate && (
                    <div>
                      <span className="text-gray-600">Data Inizio:</span>
                      <span className="ml-2 font-medium">
                        {new Date(formData.startDate).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                  {formData.completedAt && (
                    <div>
                      <span className="text-gray-600">Completata:</span>
                      <span className="ml-2 font-medium">
                        {new Date(formData.completedAt).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Azioni */}
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/practices')}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.typeId || !formData.clientId || !formData.title}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? 'Importazione...' : 'Importa Pratica Storica'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
